/**
 * Grid model - the authored format for the light-code configurator.
 *
 * Design: documentation/engineering/configurator-light-code-grid.md.
 *
 * A composition is a tree of boxes. Each box is either terminal (holds assets)
 * or carries a light code that expands into a flex grid of child boxes. A light
 * code's digits are columns (left -> right); each digit's value is the number of
 * even rows in that column (top -> bottom). Cells auto-equalize unless locked:
 * `lockedWidth` fixes a box's column-track width, `lockedHeight` fixes its
 * row-track height.
 *
 * This module is purely headless: it expands the authored `GridComposition`
 * into the internal `ShapeComposition` (nested `Opening` tree) that the proven
 * `LayoutEngine`/`ShapeRenderer` already render, and reports any
 * over-constraint as structured, human-readable issues. The renderer never sees
 * the grid format; the editor never hand-builds the nested tree.
 */

import type {
  AssetReference,
  CompositionDefaults,
  Dimension,
  Divider,
  Opening,
  OpeningStyle,
  ShapeComposition,
  Size,
} from '../types';

/** Version stamped on grid compositions. Bumped on any breaking shape change. */
export const SUPPORTED_GRID_VERSION = 'grid-1';

/**
 * The single frame weight (mm) for every configuration drawing.
 *
 * The drawing is a schematic line drawing, not a scale section, so frame weight
 * carries no information and is deliberately not authorable - there is no UI to
 * change it and no per-composition override. One constant drives all three
 * things the weight touches: the outer frame band, the glazing-area inset, and
 * the thickness of every mullion and transom. Tune here to restyle all three
 * together; they must never diverge.
 */
export const FRAME_THICKNESS_MM = 3;

/**
 * A box in the authored grid. Terminal when `lightCode` is absent; otherwise it
 * expands into a grid and per-cell content lives in `overrides`, keyed by the
 * deterministic cell id `c{col}r{row}` (1-based, row 1 = top).
 */
export interface GridBox {
  /** Display label; auto column-major when absent. */
  label?: string;
  /** Light code, e.g. "2323". Absent ⇒ terminal box. */
  lightCode?: string;
  /** Locks this box's column-track width. Absent ⇒ width auto-equalizes. */
  lockedWidth?: Dimension;
  /** Locks this box's row-track height. Absent ⇒ height auto-equalizes. */
  lockedHeight?: Dimension;
  /** Assets for a terminal box (rendered stacked). */
  assets?: AssetReference[];
  /** Per-cell overrides for a subdivided box, keyed `c{col}r{row}`. */
  overrides?: Record<string, GridBox>;
  /** Custom style for this box. */
  style?: OpeningStyle;
  /** Marks the test-candidate light (at most one across the composition). */
  testCandidate?: boolean;
}

/** The authored composition - the persisted `ConfigurationJson` shape. */
export interface GridComposition {
  /** Grid format version. Must equal {@link SUPPORTED_GRID_VERSION}. */
  version: string;
  name: string;
  description?: string;
  /** Overall size (mm). */
  size: Size;
  /** Root box. */
  root: GridBox;
  /** Global render defaults (frame thickness/color, opening style). */
  defaults?: CompositionDefaults;
  metadata?: Record<string, unknown>;
}

/** Over-constraint kinds surfaced to the user. */
export type GridConstraintCode =
  | 'all-columns-locked'
  | 'conflicting-width-lock'
  | 'all-rows-locked'
  | 'height-lock-void'
  | 'height-lock-overflow';

/** A single over-constraint, with a reason the UI can show verbatim. */
export interface GridConstraintIssue {
  code: GridConstraintCode;
  /** Opening/box path the issue applies to (e.g. "root" or "root/c2r1"). */
  boxPath: string;
  /** 0-based column index when the issue is column-scoped. */
  columnIndex?: number;
  /** Human-readable reason, e.g. "All columns are width-locked...". */
  message: string;
}

/** Result of expanding a grid: the render composition plus any issues. */
export interface GridExpansionResult {
  composition: ShapeComposition;
  issues: GridConstraintIssue[];
}

/** Outcome of reading a stored configuration string. */
export type GridDocument =
  | { kind: 'empty' }
  | { kind: 'grid'; grid: GridComposition }
  | { kind: 'unsupported'; reason: string };

/**
 * Read a stored `ConfigurationJson`. Empty ⇒ `empty` (caller starts blank).
 * Anything not at {@link SUPPORTED_GRID_VERSION} ⇒ `unsupported` (caller shows a
 * clear state) - never a parse-and-crash.
 */
export function parseGridDocument(
  json: string | null | undefined
): GridDocument {
  if (!json || json.trim().length === 0) {
    return { kind: 'empty' };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return {
      kind: 'unsupported',
      reason: 'This configuration could not be read and cannot be opened.',
    };
  }
  if (typeof raw !== 'object' || raw === null) {
    return {
      kind: 'unsupported',
      reason: 'This configuration is not in a recognised format.',
    };
  }
  const version = (raw as { version?: unknown }).version;
  if (version !== SUPPORTED_GRID_VERSION) {
    return {
      kind: 'unsupported',
      reason:
        `This configuration was made with an unsupported version ` +
        `(${version === undefined ? 'unversioned' : String(version)}) and ` +
        `can't be opened. Recreate it to use the current tool.`,
    };
  }
  return { kind: 'grid', grid: raw as GridComposition };
}

/** Build an empty single-light grid of the given mm size. */
export function createEmptyGrid(
  name: string,
  widthMM: number,
  heightMM: number,
  defaults?: CompositionDefaults
): GridComposition {
  return {
    version: SUPPORTED_GRID_VERSION,
    name,
    size: {
      width: { value: widthMM, unit: 'mm' },
      height: { value: heightMM, unit: 'mm' },
    },
    root: {},
    defaults,
  };
}

/**
 * Expand an authored grid into the internal render composition and collect any
 * over-constraint issues. Pure - no DOM, no mutation of the input.
 */
export function expandGrid(grid: GridComposition): GridExpansionResult {
  const issues: GridConstraintIssue[] = [];
  const mullion: Divider = { type: 'mullion', thickness: FRAME_THICKNESS_MM };
  const transom: Divider = { type: 'transom', thickness: FRAME_THICKNESS_MM };
  const labelCounter = { n: 1 };

  const root = expandBox(
    grid.root,
    'root',
    labelCounter,
    { mullion, transom },
    issues,
    grid.size.height.value
  );

  const composition: ShapeComposition = {
    version: grid.version,
    name: grid.name,
    description: grid.description,
    size: grid.size,
    root,
    defaults: grid.defaults,
    metadata: grid.metadata,
  };
  return { composition, issues };
}

interface Dividers {
  mullion: Divider;
  transom: Divider;
}

/** Parse a light code into per-column row counts (1-9), or null if terminal. */
function parseLightCode(code: string | undefined): number[] | null {
  if (!code) return null;
  const digits: number[] = [];
  for (const ch of code) {
    if (ch < '0' || ch > '9') continue;
    const n = parseInt(ch, 10);
    digits.push(Math.min(Math.max(n, 1), 9));
  }
  return digits.length > 0 ? digits : null;
}

function nextLabel(counter: { n: number }): string {
  return String(counter.n++).padStart(2, '0');
}

/** Apply a per-axis lock to a child opening (width for a column, height for a row). */
function applyLock(opening: Opening, lock: Dimension | undefined): void {
  if (!lock) return;
  opening.size = lock;
  opening.sizeLocked = true;
}

function makeTerminal(
  box: GridBox,
  path: string,
  labelCounter: { n: number }
): Opening {
  const opening: Opening = {
    id: path,
    label: box.label ?? nextLabel(labelCounter),
  };
  if (box.assets && box.assets.length > 0) opening.assets = box.assets;
  if (box.style) opening.style = box.style;
  if (box.testCandidate) opening.testCandidate = true;
  return opening;
}

/**
 * Resolve a column's width lock from the locks authored on its cells. Cells in
 * one column share a width track; differing locks are a conflict (first wins,
 * with an issue).
 */
function resolveColumnWidthLock(
  box: GridBox,
  columnIndex: number,
  rowCount: number,
  path: string,
  issues: GridConstraintIssue[]
): Dimension | undefined {
  const locks: Dimension[] = [];
  for (let ri = 0; ri < rowCount; ri++) {
    const cell = box.overrides?.[`c${columnIndex + 1}r${ri + 1}`];
    if (cell?.lockedWidth) locks.push(cell.lockedWidth);
  }
  if (locks.length === 0) return undefined;
  const first = locks[0];
  const conflict = locks.some(
    (l) => l.value !== first.value || l.unit !== first.unit
  );
  if (conflict) {
    issues.push({
      code: 'conflicting-width-lock',
      boxPath: path,
      columnIndex,
      message: `Column ${columnIndex + 1} has cells locked to different widths; using the first.`,
    });
  }
  return first;
}

function cellAt(box: GridBox, columnIndex: number, rowIndex: number): GridBox {
  return box.overrides?.[`c${columnIndex + 1}r${rowIndex + 1}`] ?? {};
}

/**
 * Recursively expand a box into an internal Opening (nested grid).
 *
 * `availableHeightMM` is the height budget the current box occupies. At the
 * root it is the composition height; for locked-height child cells it is their
 * lock value. Auto-sized children omit it (undefined) since we cannot know
 * their allocation without running the layout engine.
 */
function expandBox(
  box: GridBox,
  path: string,
  labelCounter: { n: number },
  dividers: Dividers,
  issues: GridConstraintIssue[],
  availableHeightMM?: number
): Opening {
  const digits = parseLightCode(box.lightCode);
  if (!digits) {
    return makeTerminal(box, path, labelCounter);
  }

  const columnCount = digits.length;
  const totalCells = digits.reduce((a, b) => a + b, 0);

  // A code that yields a single cell collapses to that cell's content.
  if (totalCells <= 1) {
    const inner = box.overrides?.['c1r1'];
    return inner
      ? expandBox(
          inner,
          path,
          labelCounter,
          dividers,
          issues,
          availableHeightMM
        )
      : makeTerminal(box, path, labelCounter);
  }

  const columns: Opening[] = [];
  const columnWidthLocks: (Dimension | undefined)[] = [];

  for (let ci = 0; ci < columnCount; ci++) {
    const rowCount = digits[ci];
    const widthLock = resolveColumnWidthLock(box, ci, rowCount, path, issues);
    columnWidthLocks.push(widthLock);

    if (rowCount === 1) {
      const cellPath = `${path}/c${ci + 1}r1`;
      const cell = cellAt(box, ci, 0);
      // Single-row cells share the parent's full height budget.
      const opening = expandBox(
        cell,
        cellPath,
        labelCounter,
        dividers,
        issues,
        availableHeightMM
      );
      applyLock(opening, widthLock);
      columns.push(opening);
      continue;
    }

    // Multi-row column: vertical nesting. Build top -> bottom (so labels and
    // cell ids run top-first), then reverse into the array because the layout
    // engine stacks child index 0 at the BOTTOM.
    const rowsTopToBottom: Opening[] = [];
    let allRowsLocked = true;
    for (let ri = 0; ri < rowCount; ri++) {
      const cellPath = `${path}/c${ci + 1}r${ri + 1}`;
      const cell = cellAt(box, ci, ri);
      // Pass the cell's locked height if known so nested expansions can validate.
      const opening = expandBox(
        cell,
        cellPath,
        labelCounter,
        dividers,
        issues,
        cell.lockedHeight?.value
      );
      if (cell.lockedHeight) applyLock(opening, cell.lockedHeight);
      else allRowsLocked = false;
      rowsTopToBottom.push(opening);
    }
    if (allRowsLocked) {
      // Detect void (underflow) or overflow when we know the available height.
      if (availableHeightMM !== undefined) {
        let lockedSum = 0;
        for (let ri = 0; ri < rowCount; ri++) {
          const cell = cellAt(box, ci, ri);
          if (cell.lockedHeight) lockedSum += cell.lockedHeight.value;
        }
        const gapsMM = (rowCount - 1) * dividers.transom.thickness;
        const totalUsed = lockedSum + gapsMM;

        if (totalUsed < availableHeightMM - 0.5) {
          issues.push({
            code: 'height-lock-void',
            boxPath: `${path}/c${ci + 1}`,
            columnIndex: ci,
            message:
              `Column ${ci + 1}: row heights total ${Math.round(lockedSum)}mm` +
              ` + ${Math.round(gapsMM)}mm frame gaps = ${Math.round(totalUsed)}mm,` +
              ` but the column is ${Math.round(availableHeightMM)}mm.` +
              ` ${Math.round(availableHeightMM - totalUsed)}mm void.` +
              ` Select the cells and use Equalize to fill evenly.`,
          });
        } else if (totalUsed > availableHeightMM + 0.5) {
          issues.push({
            code: 'height-lock-overflow',
            boxPath: `${path}/c${ci + 1}`,
            columnIndex: ci,
            message:
              `Column ${ci + 1}: row heights total ${Math.round(lockedSum)}mm` +
              ` + ${Math.round(gapsMM)}mm frame gaps = ${Math.round(totalUsed)}mm,` +
              ` but the column is only ${Math.round(availableHeightMM)}mm.` +
              ` Rows overflow by ${Math.round(totalUsed - availableHeightMM)}mm.`,
          });
        } else {
          // Heights fit exactly; still note that auto-adjustment is disabled.
          issues.push({
            code: 'all-rows-locked',
            boxPath: `${path}/c${ci + 1}`,
            columnIndex: ci,
            message: `All ${rowCount} rows in column ${ci + 1} are height-locked, so their heights can't be auto-adjusted.`,
          });
        }
      } else {
        issues.push({
          code: 'all-rows-locked',
          boxPath: `${path}/c${ci + 1}`,
          columnIndex: ci,
          message: `All ${rowCount} rows in column ${ci + 1} are height-locked, so their heights can't be auto-adjusted.`,
        });
      }
    }

    const column: Opening = {
      id: `${path}/c${ci + 1}`,
      label: '',
      nesting: { direction: 'vertical', divider: dividers.transom },
      openings: rowsTopToBottom.reverse(),
    };
    applyLock(column, widthLock);
    columns.push(column);
  }

  if (columnCount > 1 && columnWidthLocks.every((l) => l !== undefined)) {
    issues.push({
      code: 'all-columns-locked',
      boxPath: path,
      message: `All ${columnCount} columns are width-locked, so their widths can't be auto-adjusted.`,
    });
  }

  const opening: Opening = {
    id: path,
    label: box.label ?? '',
    nesting: { direction: 'horizontal', divider: dividers.mullion },
    openings: columns,
  };
  if (box.style) opening.style = box.style;
  return opening;
}
